(async () => {
  if (window.aiShoppingReady) return;
  window.aiShoppingReady = true;
  const script = document.currentScript;
  const asset = name => new URL(name, script.src).href;
  const root = window.Shopify?.routes?.root || '/';
  const currency = window.Shopify?.currency?.active || 'GBP';
  const money = cents => new Intl.NumberFormat('en-GB', {style:'currency',currency}).format(cents / 100);
  const data = Promise.all(['ai-product-facts.json','ai-shopping-labels.json'].map(name => fetch(asset(name)).then(r => {if (!r.ok) throw Error('Details unavailable'); return r.json();})));
  const [, labels] = await data;
  const t = (key, params={}) => Object.entries(params).reduce((text,[name,value])=>text.replaceAll('{'+name+'}',String(value)), labels.ui[key] || key);
  const cache = new Map();
  async function catalogue(path, refresh=false) {
    const base = new URL(path,location.origin);
    base.searchParams.delete('page');
    base.searchParams.delete('section_id');
    const key = base.href;
    if (!refresh && cache.has(key)) return cache.get(key);
    const promise = (async () => {
      let url = new URL(base); url.searchParams.set('section_id','ai-catalogue-index');
      const all = new Map(); let page = 0;
      while (url && page++ < 100) {
        const response = await fetch(url,{credentials:'same-origin'});
        if (!response.ok) throw Error('Could not load this collection');
        const doc = new DOMParser().parseFromString(await response.text(),'text/html');
        const element = doc.querySelector('[data-ai-catalogue]');
        if (!element) throw Error('Collection unavailable');
        const chunk = JSON.parse(element.textContent);
        for (const product of chunk.products) all.set(product.handle, product);
        url = chunk.next ? new URL(chunk.next,base) : null;
        if (url) url.searchParams.set('section_id','ai-catalogue-index');
      }
      if (url) throw Error('Collection is too large to load');
      return [...all.values()];
    })();
    cache.set(key,promise);
    try {return await promise;} catch (error) {cache.delete(key); throw error;}
  }
  function node(tag,text,className) {
    const el = document.createElement(tag);
    if (text != null) el.textContent = text;
    if (className) el.className = className;
    return el;
  }
  function card(product,variant,reason) {
    const article = node('article',null,'ai-shopping-card');
    const url = new URL(product.url,location.origin); url.searchParams.set('variant',variant.id);
    if (variant.image || product.image) {
      const image = node('img'); image.src = variant.image || product.image; image.alt = product.title; image.loading='lazy'; image.width=480; image.height=480;
      article.append(image);
    }
    const body = node('div',null,'ai-card-body');
    const title = node('h3'); const link=node('a',product.title); link.href=url.pathname+url.search; title.append(link); body.append(title);
    if (variant.title && variant.title !== 'Default Title') body.append(node('p',variant.title,'ai-small'));
    body.append(node('p',money(variant.price)));
    if (reason) body.append(node('p',reason,'ai-small'));
    const button=node('a',t('view'),'button'); button.href=link.href; body.append(button); article.append(body);
    return article;
  }
  function matches(variant,features,facts) {
    return variant.available && features.every(f => (facts[variant.sku]?.flags || []).includes(f));
  }
  function findVariant(product,features,facts,min=0,max=Infinity) {
    return product.variants.filter(v=>matches(v,features,facts)&&v.price>=min&&v.price<max).sort((a,b)=>a.price-b.price)[0];
  }
  const questions = [['sensation',['gentle','focused','deep','rhythmic','quiet']],['experience',['new','experienced']],['use',['solo','partner','both']],['budget',['under30','30to60','premium']],['type',['bullet','wand','rabbit','gspot','any']]].map(([key,values])=>({title:labels.quiz[key],options:values.map(value=>[value,labels.quiz[value],labels.quiz[value+'_description']])}));
  const finder=document.querySelector('[data-ai-finder]');
  if (finder) {
    const answers=[]; let step=0, generation=0;
    const questionsEl=finder.querySelector('[data-ai-questions]'), progress=finder.querySelector('[data-ai-progress]'), back=finder.querySelector('[data-ai-back]'), result=finder.querySelector('[data-ai-result]');
    function show(focus=false) {
      result.hidden=true; questionsEl.hidden=false; back.hidden=step===0;
      progress.textContent=t('question',{current:step+1,total:questions.length});
      questionsEl.replaceChildren();
      const heading=node('h2',questions[step].title); heading.tabIndex=-1; questionsEl.append(heading);
      const options=node('div',null,'ai-quiz-options');
      for (const [value,label,description] of questions[step].options) {
        const button=node('button',label); button.type='button';
        if (description) button.append(node('span',description));
        button.addEventListener('click',()=>{answers[step]=value;step++;if(step===questions.length) recommend();else show(true);}); options.append(button);
      }
      questionsEl.append(options); if(focus)heading.focus();
    }
    async function recommend() {
      const token=++generation;
      questionsEl.hidden=true; back.hidden=true; progress.textContent=t('complete'); result.hidden=false;
      const status=finder.querySelector('[data-ai-status]'), results=finder.querySelector('[data-ai-results]'), explanation=finder.querySelector('[data-ai-explanation]');
      status.textContent=t('loading'); results.replaceChildren();
      const [sense,experience,use,budget,type]=answers;
      const senseCollections={gentle:'gentle-broad-1',focused:'focused-intense',deep:'deep-internal',rhythmic:'rhythmic-pulsing',quiet:'whisper-quiet'};
      const typeCollections={bullet:'bullet-vibrators',wand:'wand-vibrators',rabbit:'rabbit-vibrators',gspot:'g-spot-vibrators'};
      const primary=typeCollections[type]||senseCollections[sense];
      const max=budget==='under30'?3000:budget==='30to60'?6001:Infinity, min=budget==='30to60'?3000:0;
      try {
        const [facts]=await data;
        const [range,beginners,couples,sensation] = await Promise.all([catalogue(`${root}collections/${primary}`),experience==='new'?catalogue(`${root}collections/beginner-friendly`):Promise.resolve([]),use==='solo'?Promise.resolve([]):catalogue(`${root}collections/couples-vibrators`),type==='any'?Promise.resolve([]):catalogue(`${root}collections/${senseCollections[sense]}`)]);
        if(token!==generation)return;
        const beginnerSet=new Set(beginners.map(p=>p.handle)),coupleSet=new Set(couples.map(p=>p.handle)),senseSet=new Set(sensation.map(p=>p.handle));
        const all=new Map(range.map(p=>[p.handle,p]));
        if(type==='any'&&experience==='new')for(const p of beginners)all.set(p.handle,p);
        if(type==='any'&&use!=='solo')for(const p of couples)all.set(p.handle,p);
        const ranked=[...all.values()].map(product=>({product,variant:findVariant(product,[],facts,min,max),score:(beginnerSet.has(product.handle)?5:0)+(coupleSet.has(product.handle)?4:0)+(senseSet.has(product.handle)?3:0)+(range.some(p=>p.handle===product.handle)?2:0)})).filter(p=>p.variant).sort((a,b)=>b.score-a.score||a.variant.price-b.variant.price);
        const picks=ranked.slice(0,3);
        explanation.textContent=t('explanation',{budget:t(budget)});
        status.textContent=picks.length?t('matches',{count:picks.length}):t('quiz_empty');
        for(const {product,variant} of picks) {
          const reasons=[];if(beginnerSet.has(product.handle))reasons.push(t('beginner_reason'));if(coupleSet.has(product.handle))reasons.push(t('couple_reason'));if(type!=='any')reasons.push(t('type_reason'));if(!reasons.length)reasons.push(t('sense_reason'));
          results.append(card(product,variant,reasons.join('. ')+'.'));
        }
      } catch(error) {if(token===generation)status.textContent=t('quiz_error');}
      const heading=result.querySelector('h2');heading.tabIndex=-1;heading.focus();
    }
    back.addEventListener('click',()=>{step=Math.max(0,step-1);show(true);});
    finder.querySelector('[data-ai-restart]').addEventListener('click',()=>{generation++;answers.length=0;step=0;show(true);});show();
  }
  const panel=document.querySelector('[data-ai-feature-panel]');
  if(panel) {
    const form=panel.querySelector('form'),results=panel.querySelector('[data-ai-feature-results]'),status=panel.querySelector('[data-ai-feature-status]'),grid=panel.querySelector('[data-ai-feature-grid]');
    let active=[],generation=0,lastUrl=location.href,failed=false;
    const native=()=>document.querySelector('#ProductGridContainer');
    async function apply() {
      const token=++generation; failed=false;
      if(!active.length){results.hidden=true;if(native())native().hidden=false;return;}
      results.hidden=false;status.textContent=t('filter_loading');grid.replaceChildren();if(native())native().hidden=true;
      try {
        const [facts]=await data;
        const [products,beginners]=await Promise.all([catalogue(location.href,true),active.includes('beginner')?catalogue(`${root}collections/beginner-friendly`):Promise.resolve([])]);
        if(token!==generation)return;
        const beginnerSet=new Set(beginners.map(p=>p.handle));let count=0;
        const params=new URL(location.href).searchParams;
        const minimum=Math.round(Number(params.get('filter.v.price.gte')||0)*100);
        const ceiling=params.has('filter.v.price.lte')?Math.round(Number(params.get('filter.v.price.lte'))*100)+1:Infinity;
        for(const product of products) {
          if(active.includes('beginner')&&!beginnerSet.has(product.handle))continue;
          const variant=findVariant(product,active.filter(f=>f!=='beginner'),facts,minimum,ceiling);
          if(!variant)continue;
          grid.append(card(product,variant,''));count++;
        }
        status.textContent=count?t('filter_count',{count}):t('filter_empty');
      } catch(error) {if(token===generation){failed=true;status.textContent=t('filter_error');if(native())native().hidden=false;}}
    }
    form.addEventListener('submit',event=>{event.preventDefault();active=[...form.querySelectorAll('input:checked')].map(i=>i.value);apply();});
    form.addEventListener('reset',()=>{active=[];apply();});
    const observer=new MutationObserver(()=>{if(lastUrl!==location.href){lastUrl=location.href;if(active.length)apply();}else if(active.length&&!failed&&native()&&!native().hidden){native().hidden=true;}});
    observer.observe(document.querySelector('main')||document.body,{childList:true,subtree:true});
    window.addEventListener('popstate',()=>{lastUrl=location.href;if(active.length)apply();});
  }
  const factBox=document.querySelector('[data-ai-product-facts]');
  if(factBox) {
    let previous=location.search, generation=0;
    const productInfo=factBox.closest('product-info')||document.querySelector('main');
    const observer=new MutationObserver(async()=>{
      if(previous===location.search)return;
      previous=location.search;
      const id=new URL(location.href).searchParams.get('variant');if(!id)return;
      const token=++generation;
      try {
      const [facts,labels]=await data;
      const product=await fetch(`${root}products/${factBox.dataset.product}.js`).then(r=>r.json());
      if(token!==generation)return;
      const selected=product.variants.find(v=>String(v.id)===id),entry=facts[selected?.sku];
      const box=document.querySelector('[data-ai-product-facts]');if(!box)return;
      box.replaceChildren();if(!entry)return;
      const details=node('details'),summary=node('summary',labels.details.heading),dl=node('dl');details.append(summary);
      for(const [key,value] of entry.rows){const row=node('div');row.append(node('dt',labels.specs[key]),node('dd',value));dl.append(row);}details.append(dl,node('p',labels.details.source,'ai-small'));box.append(details);
      } catch(error) { if(token===generation){const box=document.querySelector('[data-ai-product-facts]');if(box)box.replaceChildren();} }
    });
    observer.observe(productInfo,{childList:true,subtree:true});
  }
})().catch(() => { const finder=document.querySelector('[data-ai-finder]'); if(finder){ const p=document.createElement('p');p.textContent='The finder is temporarily unavailable. Browse the collections using the menu.';finder.append(p); } });
